import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  CheckCircle2,
  XCircle,
  ChevronDown,
  ChevronRight,
  Image as ImageIcon,
  RefreshCw,
  ExternalLink,
  Copy,
  Filter,
} from "lucide-react";

const FR_TZ = "Europe/Paris";

function formatParis(isoUtc) {
  if (!isoUtc) return "—";
  try {
    const d = new Date(isoUtc);
    return new Intl.DateTimeFormat("fr-FR", {
      timeZone: FR_TZ,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).format(d);
  } catch {
    return isoUtc;
  }
}

function relativeMinutes(isoUtc) {
  if (!isoUtc) return "";
  try {
    const diff = (Date.now() - new Date(isoUtc).getTime()) / 1000;
    if (diff < 60) return `il y a ${Math.round(diff)}s`;
    if (diff < 3600) return `il y a ${Math.round(diff / 60)} min`;
    if (diff < 86400) return `il y a ${Math.round(diff / 3600)} h`;
    return `il y a ${Math.round(diff / 86400)} j`;
  } catch {
    return "";
  }
}

export default function AdminImageLogs() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, success_count: 0, error_count: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(""); // "" | "success" | "error"
  const [expanded, setExpanded] = useState({}); // id -> bool
  const [serverNowUtc, setServerNowUtc] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "100" });
      if (statusFilter) params.set("status", statusFilter);
      const r = await api.get(`/admin/image-logs?${params}`);
      setItems(r.data.items || []);
      setStats({
        total: r.data.total || 0,
        success_count: r.data.success_count || 0,
        error_count: r.data.error_count || 0,
      });
      setServerNowUtc(r.data.now_utc);
    } catch (e) {
      toast.error("Erreur de chargement des logs");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const copyPrompt = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(
      () => toast.success("Prompt copié"),
      () => toast.error("Impossible de copier")
    );
  };

  return (
    <div data-testid="admin-image-logs-page">
      <div className="flex items-baseline justify-between flex-wrap gap-4 mb-2">
        <div>
          <p className="overline mb-2">Génération d'images · Nano Banana</p>
          <h1 className="font-serif text-4xl">Logs images IA</h1>
        </div>
        <button
          onClick={load}
          data-testid="image-logs-refresh"
          className="text-sm flex items-center gap-2 px-3 py-2 border border-[hsl(var(--border-light))] rounded-sm hover:bg-[hsl(var(--bg-subtle))] transition-colors"
        >
          <RefreshCw className="w-4 h-4" strokeWidth={1.5} /> Rafraîchir
        </button>
      </div>

      <p className="text-[hsl(var(--text-secondary))] mb-6 max-w-3xl text-sm">
        Toutes les générations d'images via Nano Banana — avec le prompt exact envoyé,
        la vignette obtenue, la durée et l'erreur le cas échéant. Les horodatages sont
        affichés en <strong>{FR_TZ}</strong> (UTC raw côté DB).
      </p>

      {/* STATS */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border border-[hsl(var(--border-light))] rounded-sm p-4" data-testid="image-stats-total">
          <p className="overline mb-1">Total</p>
          <p className="font-serif text-3xl">{stats.total}</p>
        </div>
        <div className="bg-white border border-[hsl(var(--border-light))] rounded-sm p-4" data-testid="image-stats-success">
          <p className="overline mb-1 text-[hsl(var(--gi-low))]">Succès</p>
          <p className="font-serif text-3xl">{stats.success_count}</p>
        </div>
        <div className="bg-white border border-[hsl(var(--border-light))] rounded-sm p-4" data-testid="image-stats-error">
          <p className="overline mb-1 text-[hsl(var(--brand-primary))]">Échecs</p>
          <p className="font-serif text-3xl">{stats.error_count}</p>
        </div>
        <div className="bg-white border border-[hsl(var(--border-light))] rounded-sm p-4">
          <p className="overline mb-1">Heure serveur (Paris)</p>
          <p className="font-mono text-xs text-[hsl(var(--text-muted))]">
            {serverNowUtc ? formatParis(serverNowUtc) : "—"}
          </p>
          <p className="font-mono text-xs text-[hsl(var(--text-muted))] mt-0.5">
            UTC: {serverNowUtc?.slice(0, 19).replace("T", " ") || "—"}
          </p>
        </div>
      </div>

      {/* FILTER */}
      <div className="flex items-center gap-3 mb-4">
        <Filter className="w-4 h-4 text-[hsl(var(--text-muted))]" strokeWidth={1.5} />
        <span className="text-xs text-[hsl(var(--text-muted))] uppercase tracking-wider">Filtrer</span>
        {[
          { v: "", l: "Tous" },
          { v: "success", l: "Succès" },
          { v: "error", l: "Échecs" },
        ].map((f) => (
          <button
            key={f.v}
            data-testid={`image-logs-filter-${f.v || "all"}`}
            onClick={() => setStatusFilter(f.v)}
            className={`text-xs px-3 py-1.5 rounded-sm transition-colors ${
              statusFilter === f.v
                ? "bg-[hsl(var(--brand-primary))] text-white"
                : "bg-white border border-[hsl(var(--border-light))] text-[hsl(var(--text-secondary))] hover:border-[hsl(var(--brand-primary))]"
            }`}
          >
            {f.l}
          </button>
        ))}
      </div>

      {/* LIST */}
      <div className="space-y-3">
        {loading ? (
          <div className="bg-white border border-[hsl(var(--border-light))] rounded-sm p-8 text-center text-[hsl(var(--text-muted))]">
            Chargement…
          </div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-[hsl(var(--border-light))] rounded-sm p-8 text-center text-[hsl(var(--text-muted))]" data-testid="image-logs-empty">
            Aucun log d'image. Lancez une régénération depuis <code>/admin/recipes</code> ou
            créez une recette avec génération d'image activée.
          </div>
        ) : items.map((l) => {
          const isOpen = !!expanded[l.id];
          const isSuccess = l.status === "success";
          return (
            <div
              key={l.id}
              className="bg-white border border-[hsl(var(--border-light))] rounded-sm"
              data-testid={`image-log-${l.id}`}
            >
              <button
                onClick={() => setExpanded((s) => ({ ...s, [l.id]: !s[l.id] }))}
                className="w-full text-left p-4 flex items-center gap-4 hover:bg-[hsl(var(--bg-subtle))]/40 transition-colors"
                data-testid={`image-log-toggle-${l.id}`}
              >
                {isOpen ? (
                  <ChevronDown className="w-4 h-4 text-[hsl(var(--text-muted))] flex-shrink-0" strokeWidth={1.5} />
                ) : (
                  <ChevronRight className="w-4 h-4 text-[hsl(var(--text-muted))] flex-shrink-0" strokeWidth={1.5} />
                )}

                {isSuccess ? (
                  <CheckCircle2 className="w-4 h-4 text-[hsl(var(--gi-low))] flex-shrink-0" strokeWidth={1.5} />
                ) : (
                  <XCircle className="w-4 h-4 text-[hsl(var(--brand-primary))] flex-shrink-0" strokeWidth={1.5} />
                )}

                {/* Thumbnail */}
                <div className="w-12 h-12 flex-shrink-0 bg-[hsl(var(--bg-subtle))] rounded-sm overflow-hidden flex items-center justify-center">
                  {l.target_current_image_url ? (
                    <img
                      src={l.target_current_image_url}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <ImageIcon className="w-5 h-5 text-[hsl(var(--text-muted))]" strokeWidth={1.5} />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium truncate">
                      {l.target_title || l.target_classic_recipe_name || "Recette inconnue / supprimée"}
                    </span>
                    {l.response_meta?.has_visual_hint && (
                      <span className="text-[10px] uppercase tracking-wider bg-[hsl(var(--brand-primary)/0.1)] text-[hsl(var(--brand-primary))] px-1.5 py-0.5 rounded-sm">
                        hinted
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-[hsl(var(--text-muted))] flex items-center gap-3 mt-0.5 flex-wrap">
                    <span title={`UTC: ${l.timestamp}`}>{formatParis(l.timestamp)}</span>
                    <span>· {relativeMinutes(l.timestamp)}</span>
                    {l.duration_ms != null && <span>· {(l.duration_ms / 1000).toFixed(1)}s</span>}
                    {l.response_meta?.base64_size_kb != null && (
                      <span>· {l.response_meta.base64_size_kb} Ko</span>
                    )}
                    <span>· {l.model}</span>
                  </div>
                </div>

                {l.target_slug && (
                  <Link
                    to={`/recettes/${l.target_slug}`}
                    target="_blank"
                    onClick={(e) => e.stopPropagation()}
                    className="text-xs flex items-center gap-1 text-[hsl(var(--brand-primary))] hover:underline flex-shrink-0"
                    data-testid={`image-log-link-${l.id}`}
                  >
                    Voir <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
                  </Link>
                )}
              </button>

              {isOpen && (
                <div
                  className="border-t border-[hsl(var(--border-light))] p-4 grid grid-cols-1 lg:grid-cols-3 gap-6 bg-[hsl(var(--bg-subtle))]/30"
                  data-testid={`image-log-details-${l.id}`}
                >
                  {/* Left: Image preview */}
                  <div>
                    <p className="overline mb-2">Image actuelle</p>
                    {l.target_current_image_url ? (
                      <img
                        src={l.target_current_image_url}
                        alt={l.target_title || ""}
                        className="w-full aspect-[4/3] object-cover rounded-sm border border-[hsl(var(--border-light))]"
                      />
                    ) : (
                      <div className="w-full aspect-[4/3] bg-[hsl(var(--bg-subtle))] rounded-sm flex items-center justify-center text-[hsl(var(--text-muted))] text-xs">
                        Pas d'image
                      </div>
                    )}
                    <div className="mt-3 text-xs text-[hsl(var(--text-muted))] space-y-0.5">
                      <p><strong>Recette ID:</strong> <code className="text-[10px]">{l.target_id || "—"}</code></p>
                      <p><strong>Catégorie:</strong> {l.target_category || "—"}</p>
                      <p><strong>Pays:</strong> {l.target_country || "—"}</p>
                      <p><strong>Nom canonique:</strong> {l.target_classic_recipe_name || "—"}</p>
                      {l.target_updated_at && (
                        <p><strong>Recette mise à jour:</strong> {formatParis(l.target_updated_at)}</p>
                      )}
                    </div>
                  </div>

                  {/* Middle: Prompt */}
                  <div className="lg:col-span-2">
                    <div className="flex items-center justify-between mb-2">
                      <p className="overline">Prompt envoyé à Nano Banana</p>
                      {l.prompt && (
                        <button
                          onClick={() => copyPrompt(l.prompt)}
                          data-testid={`image-log-copy-${l.id}`}
                          className="text-xs flex items-center gap-1 text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--brand-primary))]"
                        >
                          <Copy className="w-3 h-3" strokeWidth={1.5} /> Copier
                        </button>
                      )}
                    </div>
                    {l.prompt ? (
                      <pre className="text-xs font-mono whitespace-pre-wrap bg-white border border-[hsl(var(--border-light))] p-3 rounded-sm max-h-80 overflow-y-auto" data-testid={`image-log-prompt-${l.id}`}>
                        {l.prompt}
                      </pre>
                    ) : (
                      <p className="text-xs text-[hsl(var(--text-muted))] italic">
                        Prompt non enregistré (log généré avant l'ajout du champ `prompt`).
                      </p>
                    )}

                    {l.message && (
                      <div className="mt-3">
                        <p className="overline mb-1">Message provider</p>
                        <p className={`text-xs font-mono p-2 rounded-sm ${
                          isSuccess
                            ? "bg-[hsl(var(--gi-low)/0.08)] text-[hsl(var(--text-primary))]"
                            : "bg-[hsl(var(--brand-primary)/0.08)] text-[hsl(var(--brand-primary))]"
                        }`} data-testid={`image-log-message-${l.id}`}>
                          {l.message}
                        </p>
                      </div>
                    )}

                    {l.response_meta && (
                      <div className="mt-3">
                        <p className="overline mb-1">Métadata réponse</p>
                        <pre className="text-xs font-mono bg-white border border-[hsl(var(--border-light))] p-2 rounded-sm">
                          {JSON.stringify(l.response_meta, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
